import type { ReportColumn } from '../models/column.types'
import type { ReportRow } from '../models/report.types'

import {
  buildReportTablePresenterSnapshot,
  type BuildReportTablePresenterSnapshotInput,
} from './reportTablePresenterSnapshot'

import type { TableColumn } from '@/shared/ui'

export interface InlineBreakdownPresentation {
  rows: ReportRow[]
  columns: ReportColumn[]
  /** Child rows whose first-dimension cell now carries the breakdown value. */
  children: WeakSet<ReportRow>
  /** The first-dimension field that carries it, and the column that value really belongs to. */
  labelField: string | null
  breakdownColumn: ReportColumn | null
}

function labelColumn(
  columns: readonly ReportColumn[],
  breakdownField: string,
): ReportColumn | undefined {
  return columns.find(
    (column) =>
      !column.invisible && !column.numeric && !column.isImage && column.field !== breakdownField,
  )
}

/**
 * Drop the breakdown column and label each expanded child by its breakdown value under the first
 * dimension, so breakdown values read as nested rows rather than a sparse extra column.
 */
export function inlineBreakdownColumn(
  rows: readonly ReportRow[],
  columns: readonly ReportColumn[],
  breakdownField: string | null | undefined,
): InlineBreakdownPresentation {
  const children = new WeakSet<ReportRow>()
  const label = breakdownField ? labelColumn(columns, breakdownField) : undefined
  if (!breakdownField || !label) {
    return {
      rows: [...rows],
      columns: [...columns],
      children,
      labelField: null,
      breakdownColumn: null,
    }
  }

  return {
    labelField: label.field,
    // The value is a channel or a campaign, not a date: it must not go through the label column's
    // formatter. A date first column used to render every breakdown child as "-".
    breakdownColumn: columns.find((column) => column.field === breakdownField) ?? {
      field: breakdownField,
      header: breakdownField,
    },
    columns: columns.filter((column) => column.field !== breakdownField),
    rows: rows.map((row) => {
      if (!row.edges?.length) return row
      return {
        ...row,
        edges: row.edges.map((edge) => {
          const child = { ...edge, [label.field]: edge[breakdownField] }
          // The parent's link target belongs to the parent entity, not to its breakdown value.
          if (label.url !== undefined) delete child[label.url]
          children.add(child)
          return child
        }),
      }
    }),
    children,
  }
}

/** An inlined child's label cell holds the breakdown value, so it formats as that column. */
export function inlineCellColumn(
  presentation: InlineBreakdownPresentation,
  row: ReportRow,
  column: ReportColumn,
): ReportColumn {
  const { labelField, breakdownColumn, children } = presentation
  return breakdownColumn && column.field === labelField && children.has(row)
    ? breakdownColumn
    : column
}

/** On-screen table snapshot: exports keep the flat breakdown column, the table nests it. */
export function inlineBreakdownSnapshot(
  input: BuildReportTablePresenterSnapshotInput,
): InlineBreakdownPresentation {
  const snapshot = buildReportTablePresenterSnapshot(input)
  const breakdownField = input.tableType === 'matrix_table' ? null : input.breakdownField
  return inlineBreakdownColumn(snapshot.rows, snapshot.columns, breakdownField)
}

/** The inlined breakdown column is hidden, not removed: keep its saved position on reorder. */
export function restoreBreakdownColumn(
  ordered: TableColumn<ReportRow>[],
  sourceColumns: readonly ReportColumn[],
  breakdownField: string | null | undefined,
): TableColumn<ReportRow>[] {
  const index = sourceColumns.findIndex((column) => column.field === breakdownField)
  const hidden = sourceColumns[index]
  if (!hidden || ordered.some((column) => column.field === hidden.field)) return ordered
  const next = [...ordered]
  next.splice(Math.min(index, next.length), 0, { field: hidden.field, header: hidden.header })
  return next
}
